/**
 * Laboratory panel section combining analyte cards and the selected trend graph.
 */

import { useEffect, useState } from 'react';
import type { LabResultItem } from '../../../types';
import { LabMarkerColumn } from './LabMarkerColumn';
import { LabResultCard } from './LabResultCard';
import { LabTrendChart } from './LabTrendChart';

/**
 * Properties for a laboratory panel and its configured markers.
 */
interface LabPanelSectionProps {
  title: string;
  items: LabResultItem[];
}

/**
 * Determine whether a marker has a current numeric evaluation.
 *
 * @param item - Laboratory marker payload.
 * @returns True when the marker can be displayed and selected.
 */
function isEvaluated(item: LabResultItem): boolean {
  return item.evaluated !== false && item.value !== null;
}

/**
 * Choose the first graphable evaluated result, then any evaluated result.
 *
 * @param items - Available analyte cards.
 * @returns Initial selected analyte key.
 */
function initialSelection(items: LabResultItem[]): string | null {
  const evaluated = items.filter(isEvaluated);
  return evaluated.find((item) => item.history.length >= 2)?.key ?? evaluated[0]?.key ?? null;
}

/**
 * Render one reference panel with vertically stacked cards and trend details.
 *
 * @param props - Panel title and analytes.
 * @returns A laboratory panel section, or null for an empty panel.
 */
export function LabPanelSection({ title, items }: LabPanelSectionProps) {
  const [selectedKey, setSelectedKey] = useState<string | null>(() =>
    initialSelection(items),
  );

  useEffect(() => {
    if (
      !items.some(
        (item) =>
          item.key === selectedKey &&
          item.evaluated !== false &&
          item.value !== null,
      )
    ) {
      setSelectedKey(initialSelection(items));
    }
  }, [items, selectedKey]);

  if (items.length === 0) return null;
  const evaluatedItems = items.filter(isEvaluated);
  const notEvaluatedItems = items.filter((item) => !isEvaluated(item));

  if (evaluatedItems.length === 0) {
    return (
      <details className="lab-results-section lab-panel-collapsed">
        <summary className="lab-panel-collapsed-summary">
          <span className="lab-results-section-title">{title}</span>
          <span className="lab-panel-collapsed-count">
            {notEvaluatedItems.length} markers · Not evaluated
          </span>
        </summary>
        <div className="lab-panel-collapsed-content">
          {notEvaluatedItems.map((item) => (
            <LabResultCard
              key={item.key}
              item={item}
              selected={false}
            />
          ))}
        </div>
      </details>
    );
  }

  const selected = evaluatedItems.find((item) => item.key === selectedKey);

  return (
    <section className="lab-results-section">
      <h3 className="lab-results-section-title">{title}</h3>
      <div className="lab-results-split-container">
        <LabMarkerColumn
          evaluatedItems={evaluatedItems}
          notEvaluatedItems={notEvaluatedItems}
          selectedKey={selectedKey}
          onSelect={(nextItem) => setSelectedKey(nextItem.key)}
        />
        <div className="lab-trend-plot-area">
          {selected && selected.history.length >= 1 ? (
            <LabTrendChart item={selected} />
          ) : (
            <span className="lab-trend-placeholder">
              Select an evaluated marker to view its measurements
            </span>
          )}
        </div>
      </div>
    </section>
  );
}

export default LabPanelSection;
